import { createSlice } from "@reduxjs/toolkit";
import {
  defaultStep,
  getPriceRange,
  calculateHasSelectedValuesState
} from "../priceRange/selectors";
import { hasFacets } from "../../selectors";
import { getNewRangeState } from "../priceRange/reducers";
import { mapAndInsertProducts } from "../results/products";
import config from "../../../../../config/common";
import {
  extendOffsetLowerBound,
  extendOffsetUpperBound,
  getOffsetFromQuery
} from "../pagination/helpers/offset";
import getPreviousPageNumber from "../pagination/selectors/getPreviousPageNumber";
import getNextPageNumber from "../pagination/selectors/getNextPageNumber";
import { registerResultsSlice } from "../results";
import { registerFiltersSlice } from "../filters";
import { registerPriceRangeSlice } from "../priceRange/slice";
import { registerQuerySlice } from "../query";
import { registerDiagnosticsSlice } from "../diagnostics/slice";
import {
  getReceiveSearchDiagnostic,
  mergeDiagnostics
} from "../diagnostics/helpers";
import { registerUiSlice } from "../ui";
import { searchProductsAdapter } from "../../productEntityAdapter";

const pageSize = config.api.product.search.pageSize;

const mergeSelectedFacets = (existing, incoming) => {
  const map = new Map();

  const addFacetsToMap = (facets, mapToUpdate) => {
    facets.forEach(facet => {
      if (facet && facet.facetId && facet.valueId) {
        mapToUpdate.set(`${facet.facetId}:${facet.valueId}`, facet);
      }
    });
  };

  addFacetsToMap(existing, map);
  const arr = Array.isArray(incoming) ? incoming : [incoming];
  addFacetsToMap(arr, map);

  return Array.from(map.values());
};

const defaultPageSize = 72;

export const initialState = {
  searchTerm: "",
  redirectUrl: "",
  products: [],
  productsEntity: searchProductsAdapter.getInitialState(),
  facets: [],
  selectedFacets: [],
  selectedSort: {},
  error: null,
  isLoading: false,
  isNewSearch: true,
  itemCount: 0,
  offset: {
    initial: 0,
    lower: 0,
    upper: pageSize
  },
  isLoadingMore: false,
  isLoadingPrevious: false,
  ui: {
    isInitialLoading: false,
    isPagingForward: false,
    isPagingBackward: false,
    lastError: null
  },
  showLoadMore: false,
  tileToFocus: {
    first: false,
    last: false,
    pageNumber: null
  },
  range: {
    currentprice: {
      selected: {
        current: null,
        temp: null
      },
      min: 0,
      max: 0,
      step: defaultStep,
      hasSelectedValues: false
    }
  },
  diagnostics: {
    recommendationsAnalytics: {}
  },
  searchPassMeta: {
    alternateSearchTerms: []
  },
  oldSelectedFacets: [],
  personalisationParams: {},
  pageSize: defaultPageSize,
  dismissedFloors: {}
};

const shouldShowLoadMore = (offset, itemCount) => offset.upper < itemCount;

const shouldShowLoadPrevious = offset => offset.lower > 0;

const hasFilterReturnedNoResults = (newItemCount, previousState) =>
  newItemCount === 0 && hasFacets({ search: previousState });

const filterCurrentPrice = selectedFacets =>
  selectedFacets.filter(filter => filter.facetId === "currentprice");

const syncProductsEntity = state => {
  if (!state.productsEntity) {
    state.productsEntity = searchProductsAdapter.getInitialState();
  }

  searchProductsAdapter.setAll(state.productsEntity, state.products || []);
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {},
  extraReducers: builder => {
    registerUiSlice(builder);
    registerResultsSlice(builder, {
      initialState,
      extendOffsetLowerBound,
      extendOffsetUpperBound,
      mapAndInsertProducts,
      getPreviousPageNumber,
      getNextPageNumber,
      mergeDiagnostics,
      shouldShowLoadMore,
      shouldShowLoadPrevious,
      getOffsetFromQuery,
      getReceiveSearchDiagnostic,
      hasFilterReturnedNoResults,
      filterCurrentPrice,
      getNewRangeState,
      pageSize,
      syncProductsEntity
    });
    registerFiltersSlice(builder, {
      mergeSelectedFacets,
      hasFilterReturnedNoResults,
      filterCurrentPrice,
      initialState,
      calculateHasSelectedValuesState
    });
    registerPriceRangeSlice(builder, {
      calculateHasSelectedValuesState,
      getPriceRange
    });
    registerQuerySlice(builder);
    registerDiagnosticsSlice(builder, { syncProductsEntity });
  }
});

export default searchSlice.reducer;
